import { UserRole } from '@scoring/types';

export const AppKey = {
    Manager: 'manager',
    Judge: 'judge',
    HeadJudge: 'headJudge',
} as const;
export type AppKey = (typeof AppKey)[keyof typeof AppKey];

export interface AppCapabilities {
    canEditEvents: boolean;
    // head judge on a heat that is still open, live or not
    isHeadJudging: boolean;
}

// Which apps a user can open. Goes by what they can do there, not just their role: anyone who can edit an event
// can run it in the manager and impersonate its judges. Only steers the screens - the backend still checks every request.
export const appsForUser = (
    roles: readonly UserRole[] | undefined,
    { canEditEvents, isHeadJudging }: AppCapabilities
): AppKey[] => {
    if (!roles) return [];

    const isAdmin = roles.includes(UserRole.Admin);
    const isJudge = roles.includes(UserRole.Judge);
    const isOrganiser = roles.includes(UserRole.Organiser);

    const apps: AppKey[] = [];
    // Someone who only judges has nothing to do in the manager.
    if (isAdmin || isOrganiser || canEditEvents || !isJudge) apps.push(AppKey.Manager);
    if (isAdmin || isJudge || canEditEvents) apps.push(AppKey.Judge);
    if (isAdmin || isHeadJudging) apps.push(AppKey.HeadJudge);

    return apps;
};

// The app the landing page opens by itself, or null to let them pick. Someone who both judges and head judges waits
// in the head judge app until something they head is live; after that they choose.
export const landingApp = (apps: readonly AppKey[], { hasLiveHeadJudging }: { hasLiveHeadJudging: boolean }) => {
    if (apps.length === 1) return apps[0];

    const judgesAndHeadJudges = apps.length === 2 && apps.includes(AppKey.Judge) && apps.includes(AppKey.HeadJudge);
    return judgesAndHeadJudges && !hasLiveHeadJudging ? AppKey.HeadJudge : null;
};
